import { Box } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import React, { useCallback } from 'react';
import { Button, IconButton, PersonIcon, useSnackbar } from 'ui-components';
import { Feature } from '~/components/Feature';
import { FEATURE_FLAGS } from '~/constants';
import { routes } from '~/core/router';
import { MediaKit } from '~/hooks/api/media-kits/get';
import { useFetchDefaultMediaKit } from '~/hooks/api/media-kits/useFetchDefaultMediaKit';

interface Props {
  creatorId: string;
}

export const ViewDefaultMediaKitButton: React.FC<Props> = ({ creatorId }) => {
  const router = useRouter();
  const fetchDefaultMediaKit = useFetchDefaultMediaKit();
  const toast = useSnackbar();
  const isShared = router.pathname.includes('/shared/');

  const handleClick = useCallback(() => {
    const isShared = router.pathname.includes('/shared/');

    const token = isShared
      ? (router.query.id as string | undefined)
      : undefined;

    fetchDefaultMediaKit.mutate(
      { creatorId, token },
      {
        onSuccess: (mediaKit: MediaKit) => {
          const url = isShared
            ? routes.mediaKitShared(mediaKit.id)
            : routes.mediaKitBuilder(mediaKit.id);
          window.open(url, '_blank');
        },
        onError: () => {
          toast.error({
            description: 'Error fetching default media kit for this creator.',
          });
        },
      },
    );
  }, [router]);

  const Component = (
    <>
      <Box
        display={{ base: 'block', md: 'none' }}
        track-id="foam-view-default-media-kit-button-mobile"
      >
        <IconButton
          aria-label="View Profile"
          icon={<PersonIcon />}
          variant="tertiary"
          onClick={handleClick}
          isLoading={fetchDefaultMediaKit.isLoading}
        />
      </Box>
      <Box
        display={{ base: 'none', md: 'block' }}
        track-id="foam-view-default-media-kit-button-desktop"
      >
        <Button
          onClick={handleClick}
          aria-label="View Default Media Kit"
          variant="tertiary"
          leftIcon={<PersonIcon />}
          isLoading={fetchDefaultMediaKit.isLoading}
        >
          View Media Kit
        </Button>
      </Box>
    </>
  );

  if (isShared) return Component;

  return (
    <Feature name={FEATURE_FLAGS.foamMediaKitsDefault}>{Component}</Feature>
  );
};
